import { Hierarchy } from "@models/hierarchy";
import { RootEquipmentData } from "@models/root-equipment-data";
import { SiteData } from "@models/site-data";
import { SiteGroup } from "@models/site-group";
import { compareEquipmentNames } from "./natural-compare";

const DEFAULT_GROUPING_DISTANCE_KM = 100;
export const INITIAL_ZOOM = 2;

export function transformHierarchy(
  hierarchy: Hierarchy,
  maxDistanceKm: number = DEFAULT_GROUPING_DISTANCE_KM
): SiteGroup[] {
  const flatHierarchy = flattenHierarchy(hierarchy);
  const groupedPlants = groupSitesByDistance(flatHierarchy, maxDistanceKm);
  return groupedPlants;
}

export function getGroupingDistanceForZoom(zoom: number): number {
  return DEFAULT_GROUPING_DISTANCE_KM * Math.pow(2, INITIAL_ZOOM - zoom);
}

export function flattenHierarchy(hierarchy: Hierarchy): SiteData[] {
  return flattenAndMapHierarchy(hierarchy);
}

export function groupSitesByDistance(
  plants: SiteData[],
  maxDistanceKm: number = DEFAULT_GROUPING_DISTANCE_KM
): SiteGroup[] {
  return groupMotors(plants, maxDistanceKm);
}

function flattenAndMapHierarchy(hierarchy: Hierarchy): SiteData[] {
  const { continents } = hierarchy;

  const flatPlants = [] as SiteData[];

  continents.forEach((continent) => {
    continent.countries.forEach((country) => {
      const countryCode = country.code;
      country.projects.forEach((project) => {
        flatPlants.push({
          countryCode,
          name: project.name,
          projectOverviewReport: project.defaultReport,
          fullName: project.orgFullName,
          productionArea: project.productionArea,
          rootPath: project.rootPath,
          latitude: project.latitude,
          longitude: project.longitude,
          rootEquipment: [...project.systems]
            .sort((left, right) => compareEquipmentNames(left.name, right.name))
            .map(
              (s) =>
                ({
                  name: s.name,
                  productionArea: s.productionArea,
                  defaultReport: s.defaultReport,
                  performanceReport: s.performanceReport,
                  rootPath: s.rootPath,
                  status: -1,
                  systemType: s.systemType,
                }) as RootEquipmentData
            ),
        } as SiteData);
      });
    });
  });
  return mergeSites(flatPlants);
}

function mergeSites(plants: SiteData[]): SiteData[] {
  const sites = new Map<string, SiteData>();
  for (const plant of plants) {
    const existing = sites.get(plant.name);
    const equipment = new Map(
      [...(existing?.rootEquipment ?? []), ...plant.rootEquipment].map(
        (item) => [JSON.stringify([item.productionArea, item.name]), item]
      )
    );
    sites.set(plant.name, {
      ...(existing ?? plant),
      rootEquipment: [...equipment.values()].sort((left, right) =>
        compareEquipmentNames(left.name, right.name)
      ),
    });
  }
  return [...sites.values()];
}

function groupMotors(plants: SiteData[], maxDistanceKm: number): SiteGroup[] {
  plants = mergeSites(plants);
  const groupedPlants: SiteData[][] = [];
  const processedPlants = new Set<string>();

  plants.forEach((currentPlant) => {
    const currentPlantName = currentPlant.name;

    if (processedPlants.has(currentPlantName)) {
      return;
    }
    processedPlants.add(currentPlantName);

    const group = [currentPlant];
    plants.forEach((otherPlant) => {
      const otherPlantName = otherPlant.name;
      if (
        currentPlantName !== otherPlantName &&
        !processedPlants.has(otherPlantName)
      ) {
        const areCloseEnough =
          calculateDistance(
            currentPlant.latitude,
            currentPlant.longitude,
            otherPlant.latitude,
            otherPlant.longitude
          ) <= maxDistanceKm;

        if (areCloseEnough) {
          group.push(otherPlant);
          processedPlants.add(otherPlantName);
        }
      }
    });
    groupedPlants.push(group);
  });

  return groupedPlants.map((groupPlants) => {
    const firstMotor = groupPlants[0];
    return {
      latitude: firstMotor.latitude,
      longitude: firstMotor.longitude,
      plants: groupPlants,
    };
  });
}

// https://en.wikipedia.org/wiki/Haversine_formula
function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const lat = [lat1, lat2];
  const lng = [lng1, lng2];
  const earthEllipsoidLength = 6_378_137;
  const dLat = ((lat[1] - lat[0]) * Math.PI) / 180;
  const dLng = ((lng[1] - lng[0]) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat[0] * Math.PI) / 180) *
      Math.cos((lat[1] * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = earthEllipsoidLength * c;
  return Math.round(d) / 1000;
}
